/*
 * Function intended for use by PPP alone (hostname is injected via vite from PPP shopify extension)
 */
import { Block, BlockLayout, REGISTERED_APPS } from '../types';
import { updateAttributes } from '../api/shared-app-requests';
import {
  TrackingDataOfferAddedToCartPPP,
  TrackingDataOffersViewPPP,
  TrackingEventType,
} from '../types/Tracking';
import { logError, logInfo } from '../utils/logging';
import TrackingManager from './tracking-manager';
import { ShopifyAddToCartResponse } from '../api/types';
import { shouldTrackAddToCart } from '../api/utils';

const TRACK_BUTTON_OFFERS_API_ENDPOINT = '/pp-embed-button/track-offers';
const TRACK_CHECKBOX_OFFERS_API_ENDPOINT = '/pp-embed-checkbox/track-offers';

export async function sendOfferAddedToCartForPPP(
  productPagePlacementBlocks: Record<string, Block>,
  isIndependentATC: boolean = false,
  variantIdAddedToCart: number | null = null,
  data?: ShopifyAddToCartResponse,
) {
  // Always update attributes for slider cart functionality
  const cartToken = await TrackingManager.getInstance()._getCartToken();
  await updateAttributes({
    candyrack_token: cartToken,
  });

  // Only track if add to cart was successful (no status field in JSON body = success)
  const addedSuccessfully = shouldTrackAddToCart(data);
  if (!addedSuccessfully) {
    return;
  }

  for (const blockKey in productPagePlacementBlocks) {
    const block = productPagePlacementBlocks[blockKey];

    const lineItems = mapOffersToAddToCartTrackerDataForPPP(
      {
        [blockKey]: block,
      },
      variantIdAddedToCart,
    );

    await trackForPPP<TrackingDataOfferAddedToCartPPP>(
      {
        event_type: TrackingEventType.ADD_TO_CART,
        cart_token: cartToken,
        line_items: lineItems,
        added_via_candyrack_button: isIndependentATC,
      },
      block.blockLayout,
    );
  }
}

/*
 * Function intended for use by PPP alone (hostname is injected via vite from PPP shopify extension)
 */
async function trackForPPP<T>(data: T, blockLayout: BlockLayout) {
  const url =
    blockLayout === 'button'
      ? `${TrackingManager.getInstance()._getApiEndpoint()}${TRACK_BUTTON_OFFERS_API_ENDPOINT}`
      : `${TrackingManager.getInstance()._getApiEndpoint()}${TRACK_CHECKBOX_OFFERS_API_ENDPOINT}`;

  try {
    const resp = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        'x-shop': window.Shopify.shop,
      },
      body: JSON.stringify(data),
    });
    const respJSON = await resp.json();
    logInfo(
      `Tracked successfully: ${respJSON}`,
      REGISTERED_APPS.PRODUCT_PAGE_PLACEMENT,
    );
  } catch (error) {
    logError('Error tracking: ', REGISTERED_APPS.PRODUCT_PAGE_PLACEMENT, error);
  }
}

const mapOffersToAddToCartTrackerDataForPPP = (
  productPagePlacementBlocks: Record<string, Block>,
  variantIdAddedToCart: number | null = null,
): TrackingDataOfferAddedToCartPPP['line_items'] => {
  const offersTrack: TrackingDataOfferAddedToCartPPP['line_items'] = [];
  Object.keys(productPagePlacementBlocks).forEach((blockKey) => {
    const blockOffers = productPagePlacementBlocks[blockKey].offers;
    const parentProduct = productPagePlacementBlocks[blockKey].product;
    blockOffers?.forEach((offer) => {
      offer.items.forEach((offerItem) => {
        if (
          offerItem.isSelected &&
          (variantIdAddedToCart === null ||
            variantIdAddedToCart === offerItem.selectedVariantId)
        ) {
          offersTrack.push({
            offer_id: String(offer.id) as string,
            variant_id: String(offerItem.selectedVariantId),
            product_id: offerItem.product.id,
            handle: offerItem.product.handle || String(offerItem.product.id),
            parent_product: {
              id: String(parentProduct.id),
              handle: parentProduct.handle,
            },
            quantity: offerItem.selectedVariantQuantity as number,
          });
        }
      });
    });
  });
  return offersTrack;
};

export async function sendOffersViewEventForPPP(
  block: Block,
  offers: { id: number; product_ids: number[] }[],
) {
  const token = await TrackingManager.getInstance()._getCartToken();
  return trackForPPP<TrackingDataOffersViewPPP>(
    {
      event_type: TrackingEventType.VIEW,
      cart_token: token,
      offers,
    },
    block.blockLayout,
  );
}

// Export for testing only
export const __test__ = {
  trackForPPP,
};
